// Persists the most-recently-loaded RDF file to the Origin Private File System
// so the graph store can be re-hydrated on cold start. Stores raw bytes (lossless
// and fast) alongside a metadata sidecar so the original filename + MIME type +
// RDF serialization survive a reload.

import {
  formatFromFileName,
  DEFAULT_RDF_FORMAT,
  type RdfFormat,
} from "graph-store"

const DATA_FILE = "loaded-data"
const META_FILE = "loaded-data.meta.json"

export interface Metadata {
  name: string
  type: string
  size: number
  savedAt: number
  sourceUrl?: string
  // RDF serialization of the persisted bytes, so cold-start rehydrate loads
  // them with the right parser. Absent in data persisted before this field
  // existed — load() falls back to the file name, then Turtle.
  format?: RdfFormat
}

export function isSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.storage !== "undefined" &&
    typeof navigator.storage.getDirectory === "function"
  )
}

async function getRoot(): Promise<FileSystemDirectoryHandle | null> {
  if (!isSupported()) return null
  try {
    return await navigator.storage.getDirectory()
  } catch {
    return null
  }
}

async function readMetadata(
  root: FileSystemDirectoryHandle,
): Promise<Metadata | null> {
  try {
    const handle = await root.getFileHandle(META_FILE)
    const file = await handle.getFile()
    const text = await file.text()
    return JSON.parse(text) as Metadata
  } catch {
    return null
  }
}

export async function getMetadata(): Promise<Metadata | null> {
  const root = await getRoot()
  if (!root) return null
  return readMetadata(root)
}

export async function has(): Promise<boolean> {
  const root = await getRoot()
  if (!root) return false
  try {
    await root.getFileHandle(DATA_FILE)
    return true
  } catch {
    return false
  }
}

export async function save(
  file: File,
  sourceUrl?: string,
  format?: RdfFormat,
): Promise<void> {
  const root = await getRoot()
  if (!root) return

  const dataHandle = await root.getFileHandle(DATA_FILE, { create: true })
  const dataWriter = await dataHandle.createWritable()
  await dataWriter.write(file)
  await dataWriter.close()

  const meta: Metadata = {
    name: file.name,
    type: file.type,
    size: file.size,
    savedAt: Date.now(),
    ...(sourceUrl !== undefined ? { sourceUrl } : {}),
    ...(format !== undefined ? { format } : {}),
  }
  const metaHandle = await root.getFileHandle(META_FILE, { create: true })
  const metaWriter = await metaHandle.createWritable()
  await metaWriter.write(JSON.stringify(meta))
  await metaWriter.close()
}

export async function load(): Promise<{
  file: File
  format: RdfFormat
} | null> {
  const root = await getRoot()
  if (!root) return null

  let dataFile: File
  try {
    const handle = await root.getFileHandle(DATA_FILE)
    dataFile = await handle.getFile()
  } catch {
    return null
  }

  const meta = await readMetadata(root)
  const name = meta?.name ?? "persisted-data"
  const type = meta?.type ?? ""
  // Prefer the recorded format; fall back to the file name (e.g. legacy data
  // persisted as `*.ttl` before `format` was stored), then Turtle.
  const format = meta?.format ?? formatFromFileName(name) ?? DEFAULT_RDF_FORMAT

  return { file: new File([dataFile], name, { type }), format }
}

export async function clear(): Promise<void> {
  const root = await getRoot()
  if (!root) return
  for (const name of [DATA_FILE, META_FILE]) {
    try {
      await root.removeEntry(name)
    } catch {
      // Missing entry is fine — clear() is idempotent.
    }
  }
}
